import {
  AvailableDate,
  DetailedTimeslot,
  EnumOptionKind,
  Option,
  OptionPriceCalculation,
  Price,
} from 'src/types';
import { BookingFormValues } from './BookingForm';
import { useFormikContext } from 'formik';
import { Modal } from 'react-bootstrap';
import { getTotalQty, isStayOrFlexible } from 'src/helpers';
import { FiX } from 'react-icons/fi';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import OptionDatepicker, { OptionAvailabilityChecker } from './OptionDatepicker';
import flatpickr from 'flatpickr';
import { DBDateFormat } from 'src/lib/constants';
import dayjs, { locale } from 'dayjs';
import BookingTimeslotSelectorV2 from './BookingTimeslotSelectorV2';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import Money from './Money';
import { useLocale, useTranslations } from 'next-intl';
import DisplaySelectedInfo from './DisplaySelectedInfo';
import DisplayBookingFormPriceUnit from 'src/components/DisplayBookingFormPriceUnit';

type Props = {
  option: Option;
  currentPrice: Price;
  prices: Price[];
  availableDates: AvailableDate[];
  detailedTimeslots: DetailedTimeslot[];
  availabilityChecker: OptionAvailabilityChecker;
  totalPriceCalculation: OptionPriceCalculation;
  canShowFromCopy?: boolean;
  isGiftExperience?: boolean;
  numSelectedAccommodationRoom?: number;
  timeslotDate: string;
  showModalDate: boolean;
  isOpenTimeslotSelector: boolean;
  onExited?: () => void;
  onSelectTimeSlot?: (timeslot: DetailedTimeslot) => void;
  openTimeslotSelector: () => void;
  onSelectDate?: (startDate: string, endDate: string | null) => void;
  setTimeslotDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsOpenTimeslotSelector: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModalDate: React.Dispatch<React.SetStateAction<boolean>>;
  formDatepickerRef: React.MutableRefObject<flatpickr.Instance | undefined>
};

const ModalSelectAvailabilitySlot: React.FC<Props> = ({
  option,
  prices,
  availableDates,
  detailedTimeslots,
  isGiftExperience,
  showModalDate,
  isOpenTimeslotSelector,
  timeslotDate,
  availabilityChecker,
  currentPrice,
  canShowFromCopy,
  totalPriceCalculation,
  numSelectedAccommodationRoom,
  onExited,
  onSelectTimeSlot,
  openTimeslotSelector,
  onSelectDate,
  setIsOpenTimeslotSelector,
  setTimeslotDate,
  setShowModalDate,
  formDatepickerRef
}) => {
  const {
    values: bookingFormValues,
    setFieldValue,
    validateForm,
    setFieldTouched,
  } = useFormikContext<BookingFormValues>();
  const locale = useLocale();
  const translationBookingForm = useTranslations('components.BookingForm')
  const translation = useTranslations('components.ModalSelectAvailabilitySlot')
  const currentCurrency = useCurrentCurrency();
  const [noWarnUnavailable, setNoWarnUnavailable] = useState(false);
  const totalQty = getTotalQty(bookingFormValues);
  const isCurrentDateUnavailable = bookingFormValues.startDate
    ? availabilityChecker.isTryLuck(bookingFormValues.startDate) ||
      availabilityChecker.hasNoAvailableTimeslotByDate(bookingFormValues.startDate)
    : false;

  return (
    <>
      <Modal
        show={showModalDate}
        onExited={() => {
          if (typeof onExited === 'function') {
            onExited();
          }
          setFieldTouched('startDate', true);
          setFieldTouched('endDate', true);
        }}
        onHide={() => {
          setShowModalDate(false);
        }}
        onShow={() => {
          setTimeout(() => {
            if (
              bookingFormValues.startDate &&
              !bookingFormValues.detailedTimeslotId &&
              !isOpenTimeslotSelector
            ) {
              openTimeslotSelector();
            }
          }, 200);
        }}
        className="fade modal-styled"
        tabIndex={-1}
        dialogClassName="modal-full-width--sp modal-dialog-scrollable modal-dialog-centered"
      >
        <Modal.Header>
          <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
            {`${translation('choose')} ${isStayOrFlexible(option) ? translation('yourDates') : translation('startDate')}`}
          </div>
          <span className="tw-cursor-pointer" onClick={() => setShowModalDate(false)}>
            <FiX size={24} strokeWidth={1} className="tw-text-brand-navy" />
          </span>
        </Modal.Header>
        <Modal.Body>
          <div className={classNames('option-datepicker tw-mx-auto')}>
            <div
              className={classNames('toast-warning tw-mb-4 md:tw-mb-2', {
                'tw-hidden': !noWarnUnavailable || !isCurrentDateUnavailable,
              })}
            >
              <div>
                <strong>{translation('thisDateIsUnavailable')}.</strong>{' '}
                <span>{translation('tryToChangeDate')}</span>
              </div>
              <button type="button" className="close" onClick={() => setNoWarnUnavailable(true)}>
                <FiX size={24} strokeWidth={1} className="tw-text-grey-light" />
              </button>
            </div>

            <OptionDatepicker
              instanceRef={formDatepickerRef}
              flatpickrClassName="tw-mx-auto"
              availableDates={availableDates}
              option={option}
              prices={prices}
              currentCurrency={currentCurrency}
              bookingFormValues={bookingFormValues}
              detailedTimeslots={detailedTimeslots}
              onMonthChange={() => {
                if (option.kind === EnumOptionKind.shortAdventure) {
                  setTimeslotDate(undefined);
                }
              }}
              datepickerProps={{
                mode:
                  option.kind === EnumOptionKind.flexibleAdventure
                    ? 'range'
                    : option.kind === EnumOptionKind.stay
                    ? 'multiple'
                    : 'single',
                allowInvalidPreload: true,
                defaultDate: [
                  bookingFormValues.startDate ? dayjs(bookingFormValues.startDate).toDate() : null,
                  bookingFormValues.endDate ? dayjs(bookingFormValues.endDate).toDate() : null,
                ].filter((v) => v),
              }}
              onChange={(dates, instance) => {
                if (option.kind === EnumOptionKind.shortAdventure) {
                  const dateStr = dayjs(dates[0]).format(DBDateFormat);
                  if (timeslotDate !== dateStr) {
                    setIsOpenTimeslotSelector(true);
                    setTimeslotDate(dateStr);
                    setFieldValue('detailedTimeslotId', null);
                    setFieldValue('startDate', dateStr);
                    setTimeout(() => {
                      const ele = document.querySelector('.option-datepicker')?.parentElement;
                      if (ele) {
                        ele.scrollTop = 300;
                      }
                    });
                  }
                }

                if (dates.length > 2) {
                  const tmpDates = dates.slice(2, 3);
                  instance.setDate(tmpDates);
                }

                if (typeof onSelectDate === 'function') {
                  onSelectDate(
                    dayjs(dates[0]).format(DBDateFormat),
                    dates[1] ? dayjs(dates[1]).format(DBDateFormat) : null
                  );
                }
                setFieldValue('startDate', dates[0] ? dayjs(dates[0]).format(DBDateFormat) : null);
                setFieldValue('endDate', dates[1] ? dayjs(dates[1]).format(DBDateFormat) : null);
              }}
            />
            {isOpenTimeslotSelector && !!timeslotDate ? (
              <div className="tw-border-t tw-border-grey-lighter tw-my-6"></div>
            ) : null}
            <BookingTimeslotSelectorV2
              className="tw-mt-3 tw-pl-2"
              titleClassName="tw-text-center"
              slotContainerClassName="tw-justify-center"
              key={timeslotDate}
              show={isOpenTimeslotSelector && !!timeslotDate}
              timeslotDate={timeslotDate}
              timeslots={
                timeslotDate ? availabilityChecker.getAvailableTimeslotByDate(timeslotDate) : []
              }
              detailedTimeslotId={bookingFormValues.detailedTimeslotId || undefined}
              onSelect={(timeslot) => {
                setFieldValue('detailedTimeslotId', timeslot.id);
                setFieldValue('startDate', timeslotDate);
                validateForm();

                if (typeof onSelectTimeSlot === 'function') {
                  onSelectTimeSlot(timeslot);
                }
              }}
            />

            {bookingFormValues.startDate && (
              <>
                <div className="tw-border-t tw-border-grey-lighter tw-my-6"></div>
                <div
                  className="tw-font-semibold tw-font-styleSans tw-text-grey tw-lowecase tw-underline tw-cursor-pointer tw-text-center"
                  onClick={() => {
                    setFieldValue('startDate', null);
                    setFieldValue('endDate', null);
                  }}
                >
                  {translation('clearSelection')}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          className={classNames({
            'tw-hidden':
              !isGiftExperience &&
              (!bookingFormValues.startDate ||
                (detailedTimeslots.length > 0 && !bookingFormValues.detailedTimeslotId)),
          })}
        >
          <div className="tw-flex tw-justify-between tw-items-center tw-w-full tw-px-1.5">
            <div className="">
              {currentPrice ? (
                <div className="font-weight-bold tw-text-sm md:tw-text-base">
                  {translation.rich(canShowFromCopy ? 'priceFrom' : 'priceTotal', {
                    money: (chunks: React.ReactNode) => (
                      <Money
                        amount={
                          totalPriceCalculation && !canShowFromCopy
                            ? totalPriceCalculation.totalPrice
                            : currentPrice.price
                        }
                        toCurrency={currentCurrency}
                        hasConversionFee={!isGiftExperience && option.baseCurrency !== currentCurrency}
                      />
                    ),
                  })}
                  {canShowFromCopy && numSelectedAccommodationRoom
                    ? <DisplayBookingFormPriceUnit option={option} totalQty={totalQty} />
                    : null}
                </div>
              ) : null}
              <div className="tw-text-grey tw-text-sm">
                <DisplaySelectedInfo
                  option={option}
                  bookingFormValues={bookingFormValues}
                  detailedTimeslots={detailedTimeslots}
                />
              </div>
            </div>
            <div className="ml-auto ml-md-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowModalDate(false)}
              >
                {translation('select')}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalSelectAvailabilitySlot;
